import { useState } from 'react';
import { Box, Button, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { zodResolver } from '../lib/zod-resolver';
import { requestPasswordReset } from '../lib/auth';
import classes from './LoginPage.module.css';

const schema = z.object({
  email: z.email('Введіть коректний email'),
});

/**
 * Anti-enumeration: the confirmation text is the same whether the address exists in the
 * system or not, and it appears the moment the request completes — not sooner, not with a
 * different wording. `error` is reserved for the one case the server never causes on
 * purpose: the request never reached it at all.
 */
export function ForgotPasswordPage(): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const form = useForm({
    initialValues: { email: '' },
    validate: zodResolver(schema),
  });

  const submit = form.onSubmit(async (values) => {
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(values.email);
      setSent(true);
    } catch {
      setError('Не вдалося надіслати запит. Перевірте зʼєднання і спробуйте ще раз.');
    } finally {
      setBusy(false);
    }
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.card} data-testid="forgot-card">
        <Stack gap={4} mb="lg">
          <Box className={classes.logo}>S</Box>
          <Text className={classes.title}>SEO Ops Core</Text>
          <Text className={classes.subtitle}>відновлення пароля</Text>
        </Stack>

        {sent ? (
          <Text data-testid="forgot-sent">
            Якщо така пошта є в системі, ми надіслали лист із посиланням
          </Text>
        ) : (
          <form onSubmit={submit} noValidate>
            <Stack gap="sm">
              <TextInput
                label="Email"
                type="email"
                autoComplete="username"
                data-testid="forgot-email"
                {...form.getInputProps('email')}
              />
              {error ? (
                <Text className={classes.error} data-testid="forgot-error" role="alert">
                  {error}
                </Text>
              ) : null}
              <Button type="submit" loading={busy} fullWidth data-testid="forgot-submit">
                Надіслати
              </Button>
              <Button
                component="a"
                href="/login"
                variant="subtle"
                fullWidth
                data-testid="forgot-back"
              >
                Повернутись до входу
              </Button>
            </Stack>
          </form>
        )}
      </Box>
    </Box>
  );
}
